import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Container,
  Paper,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import { Download as DownloadIcon, CloudDownload as CloudDownloadIcon } from '@mui/icons-material';
import { userService } from '../services/userService';

interface UserSvg {
  productId: string | { _id: string; name?: string } | any;
  name?: string;
  productName?: string;
  fileName?: string;
  description?: string;
  price?: number;
  category?: string;
  tags?: string[];
  previewUrl?: string;
  fileSize?: string;
  downloads?: number;
  rating?: number;
  purchaseDate?: string;
  status?: string;
  downloadUrl?: string;
  downloadsRemaining?: number;
  maxDownloads?: number;
  expiresAt?: string;
}

interface UserDownloadsProps {
  userSvgs: UserSvg[];
  onDownload: (productId: string) => void;
  downloading: string | null;
  onRequireLogin?: () => void;
}

const UserDownloads: React.FC<UserDownloadsProps> = ({ userSvgs, onDownload, downloading, onRequireLogin }) => {
  const [downloads, setDownloads] = useState<UserSvg[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Downloads state is managed by useState with proper initialization

  // Additional safety - ensure state is always valid
  React.useEffect(() => {
    if (!Array.isArray(downloads)) {
      console.error('🔍 UserDownloads: Invalid downloads state detected, resetting:', downloads);
      setDownloads([]);
    }
  }, [downloads]);

  useEffect(() => {
    // Check authentication first
    if (!userService.isAuthenticated()) {
      if (onRequireLogin) {
        onRequireLogin();
      }
      setLoading(false);
      return;
    }
    fetchUserDownloads();
  }, [onRequireLogin]);

  const fetchUserDownloads = async () => {
    try {
      setLoading(true);
      const authToken = localStorage.getItem('authToken');
      
      // Verification logging
      console.log('🔍 UserDownloads: Starting fetchUserDownloads');
      console.log('🔍 Auth token exists:', !!authToken);
      console.log('🔍 Auth token value:', authToken ? 'Present' : 'Missing');
      
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/downloads/user`, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });
      
      console.log('🔍 API Response status:', response.status);
      console.log('🔍 API Response ok:', response.ok);
      
      if (response.ok) {
        const data = await response.json();
        console.log('🔍 API Response data:', data);
        console.log('🔍 Downloads array:', data.downloads);
        console.log('🔍 Downloads length:', data.downloads?.length || 0);
        console.log('🔍 Response success:', data.success);
        
        if (data.success && data.downloads) {
          // Normalize download data to ensure name is available
          const normalizedDownloads = data.downloads.map((download: any) => ({
            ...download,
            name: download.name || download.productName || (download.productId?.name) || download.fileName || 'Unknown Product'
          }));
          setDownloads(normalizedDownloads);
        } else {
          console.log('🔍 No downloads in response, setting empty array');
          setDownloads([]);
        }
      } else {
        const errorData = await response.json();
        console.error('🔍 API Error response:', errorData);
        setError(`Failed to fetch downloads: ${errorData.error || 'Unknown error'}`);
        setDownloads([]); // Ensure downloads is always an array
      }
    } catch (error) {
      console.error('🔍 Network/Other error:', error);
      setError('Error fetching downloads');
      setDownloads([]); // Ensure downloads is always an array
    } finally {
      setLoading(false);
    }
  };


  // Show sign-in prompt if not authenticated
  if (!userService.isAuthenticated()) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper 
          sx={{ 
            p: 4, 
            bgcolor: 'background.paper', 
            borderRadius: 3,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            border: '1px solid',
            borderColor: 'grey.200',
            textAlign: 'center'
          }}
        >
          <CloudDownloadIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography 
            variant="h4" 
            sx={{ 
              mb: 1, 
              fontWeight: 700,
              background: 'linear-gradient(45deg, #1976d2, #42a5f5)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            Sign in to access past downloads
          </Typography>
          <Typography 
            variant="h6" 
            sx={{ 
              color: 'text.secondary',
              fontWeight: 400
            }}
          >
            Sign in or register with your email address to view your purchased SVG designs
          </Typography>
        </Paper>
      </Container>
    );
    }

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading your downloads...
        </Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">
          {error}
        </Alert>
      </Container>
    );
  }

  // Simplified render function with better error handling
  const renderDownloads = () => {
    console.log('🔍 UserDownloads: Rendering downloads, state:', { downloads, loading, error });
    
    // Ensure downloads is always an array
    const validDownloads = Array.isArray(downloads) ? downloads : [];
    console.log('🔍 UserDownloads: Valid downloads:', validDownloads);
    
    if (validDownloads.length === 0) {
      return (
        <Box sx={{ 
          textAlign: 'center', 
          py: 8,
          bgcolor: 'grey.50',
          borderRadius: 3,
          border: '2px dashed',
          borderColor: 'grey.300'
        }}>
          <CloudDownloadIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h5" color="text.secondary" sx={{ mb: 2, fontWeight: 600 }}>
            No downloads available
          </Typography>
          <Typography color="text.secondary" sx={{ maxWidth: 400, mx: 'auto' }}>
            Purchase digital products to access downloads here. Start exploring our SVG collection!
          </Typography>
        </Box>
      );
    }

    return (
      <Grid container spacing={3}>
        {validDownloads.map((svg, index) => {
          try {
            return (
              <Grid item xs={12} sm={6} md={3} key={svg.productId || `download-${index}`}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    bgcolor: 'background.paper',
                    boxShadow: 2,
                    borderRadius: 3,
                    transition: 'box-shadow 0.2s, transform 0.2s',
                    '&:hover': { 
                      boxShadow: 4,
                      transform: 'translateY(-2px)'
                    },
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 3 }}>
                    <Typography variant="h6" sx={{ 
                      fontWeight: 700, 
                      mb: 2,
                      fontSize: '1.1rem',
                      color: 'text.primary'
                    }}>
                      {svg.name || svg.productName || (svg.productId && typeof svg.productId === 'object' && 'name' in svg.productId ? (svg.productId as { name: string }).name : null) || svg.fileName || `Download ${index + 1}`}
                    </Typography>
                    
                    <Box sx={{ flexGrow: 1, mb: 3 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Downloads remaining: {svg.downloadsRemaining || svg.maxDownloads || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Expires: {svg.expiresAt ? new Date(svg.expiresAt).toLocaleDateString() : 'N/A'}
                      </Typography>
                    </Box>

                    <Box sx={{ mt: 'auto' }}>
                      <Button
                        variant="contained"
                        fullWidth
                        startIcon={<DownloadIcon />}
                        onClick={async () => {
                          // Extract productId - handle both string and object formats
                          const productId = typeof svg.productId === 'string' 
                            ? svg.productId 
                            : (svg.productId as { _id: string })?._id || svg.productId;
                          
                          if (!productId) {
                            console.error('Product ID missing for download');
                            return;
                          }
                          
                          // Use the onDownload prop which properly increments download count via API
                          if (onDownload) {
                            try {
                              await onDownload(productId);
                              // Refresh downloads list after successful download to update remaining count
                              setTimeout(() => {
                                fetchUserDownloads();
                              }, 500); // Small delay to ensure backend has updated
                            } catch (error) {
                              console.error('Download failed:', error);
                            }
                          }
                        }}
                        disabled={downloading === svg.productId || (svg.downloadsRemaining || 0) <= 0}
                        sx={{ 
                          backgroundColor: 'success.main',
                          fontWeight: 600,
                          py: 1.5,
                          '&:hover': {
                            backgroundColor: 'success.dark',
                          }
                        }}
                      >
                        {downloading === svg.productId
                          ? 'Downloading...'
                          : (svg.downloadsRemaining || 0) > 0
                          ? 'Download File'
                          : 'No Downloads Left'}
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          } catch (itemError) {
            console.error('🔍 UserDownloads: Error rendering download item:', itemError, svg);
            return (
              <Grid item xs={12} sm={6} md={3} key={`error-${index}`}>
                <Card sx={{ p: 2, bgcolor: 'error.light', textAlign: 'center' }}>
                  <Typography color="error">
                    Error loading download item
                  </Typography>
                </Card>
              </Grid>
            );
          }
        })}
      </Grid>
    );
  };

  return (
  <Container maxWidth="lg" sx={{ py: 4 }}>
    <Paper 
      sx={{ 
        p: 4, 
        bgcolor: 'background.paper', 
        borderRadius: 3,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        border: '1px solid',
        borderColor: 'grey.200'
      }}
    >
      {/* Section Title */}
      <Typography 
        variant="h4" 
        sx={{ 
          mb: 1, 
          fontWeight: 700,
          textAlign: 'center',
          background: 'linear-gradient(45deg, #1976d2, #42a5f5)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent'
        }}
      >
        My Downloads
      </Typography>
      
      <Typography 
        variant="h6" 
        sx={{ 
          mb: 4, 
          textAlign: 'center',
          color: 'text.secondary',
          fontWeight: 400
        }}
      >
        Access your purchased SVG designs
      </Typography>

      {renderDownloads()}

    </Paper>
  </Container>
);
};

export default UserDownloads; 